import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Card from './Card.jsx';
import { C } from '../utils/theme.js';

// Fixed-height label/value slots keep every box in a grid row perfectly aligned,
// regardless of the label wrapping to two lines or the box having no `sub`.
var LABEL_H = 30;
var VALUE_H = 30;
var TIP_W = 270;

var clamp2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden"
};

// Anchor the tooltip to the card, clamped to the viewport. Rendered in a portal
// with position:fixed so no ancestor's overflow:hidden can clip it.
function tipPlacement(rect) {
  var cx = rect.left + rect.width / 2;
  var below = rect.top < 150;
  return {
    cx: cx,
    x: Math.min(Math.max(cx, TIP_W / 2 + 10), window.innerWidth - TIP_W / 2 - 10),
    y: below ? rect.bottom + 10 : rect.top - 10,
    below: below
  };
}

export default function StatBox(props) {
  var color = props.color || C.a1;
  var raw = (props.value === 0 || props.value) ? String(props.value) : "-";
  // Shrink long values ("13.21 MHz", "1,234 km") so they never crowd the card edges
  var vSize = raw.length <= 6 ? 24 : raw.length <= 9 ? 20 : raw.length <= 13 ? 17 : 15;

  var tip = props.tip;
  var posSt = useState(null); var pos = posSt[0]; var setPos = posSt[1];
  var boxRef = useRef(null);

  // A fixed-position tooltip would drift away from its card on scroll — hide it instead
  useEffect(function() {
    if (!pos) return;
    var hide = function() { setPos(null); };
    window.addEventListener("scroll", hide, true);
    return function() { window.removeEventListener("scroll", hide, true); };
  }, [pos]);

  function show() {
    if (!tip || !boxRef.current) return;
    setPos(tipPlacement(boxRef.current.getBoundingClientRect()));
  }
  function hide() { setPos(null); }

  var arrowLeft = pos ? Math.min(Math.max(pos.cx - pos.x + TIP_W / 2, 16), TIP_W - 16) : TIP_W / 2;

  return (
    <div
      ref={boxRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={tip ? 0 : undefined}
      style={{ position: "relative", outline: "none", cursor: tip ? "help" : "default" }}
    >
      <Card style={{
        padding: "12px 14px 14px",
        borderLeft: "3px solid " + color,
        borderTop: "1px solid " + (pos ? color + "66" : C.bd),
        borderRight: "1px solid " + (pos ? color + "66" : C.bd),
        borderBottom: "1px solid " + (pos ? color + "66" : C.bd),
        background: "linear-gradient(90deg," + color + (pos ? "22" : "14") + " 0%,transparent 55%),"
          + "linear-gradient(135deg," + C.s1 + "," + C.s2 + ")",
        transition: "border-color .15s, background .15s",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        textAlign: "center"
      }}>
        <div style={Object.assign({
          height: LABEL_H,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          lineHeight: 1.25,
          color: C.t2,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          fontWeight: 600
        }, clamp2)}>
          {props.icon ? props.icon + " " : ""}{props.label}
          {tip ? <span style={{ marginLeft: 4, fontSize: 9, color: C.t3, verticalAlign: "super" }}>{"ⓘ"}</span> : null}
        </div>

        <div title={raw} style={{
          height: VALUE_H,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: vSize,
          fontWeight: 800,
          color: color,
          fontFamily: "'Courier New',monospace",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: 0.5,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          {raw}
        </div>

        {props.sub ? (
          <div style={Object.assign({
            marginTop: 4,
            fontSize: 10.5,
            lineHeight: 1.3,
            color: C.t3
          }, clamp2)}>
            {props.sub}
          </div>
        ) : null}
      </Card>

      {pos && typeof document !== "undefined" ? createPortal(
        <div style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: TIP_W,
          transform: pos.below ? "translate(-50%,0)" : "translate(-50%,-100%)",
          zIndex: 4000,
          pointerEvents: "none",
          background: C.s2,
          border: "1px solid " + color + "66",
          borderRadius: 10,
          boxShadow: "0 8px 24px #00000070, 0 0 16px " + color + "20",
          padding: "10px 12px",
          textAlign: "left"
        }}>
          <div style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: color,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            marginBottom: 5
          }}>
            {props.label}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: C.t1 }}>{tip}</div>
          <div style={{
            position: "absolute",
            left: arrowLeft,
            [pos.below ? "top" : "bottom"]: -5,
            marginLeft: -5,
            width: 9,
            height: 9,
            background: C.s2,
            borderRight: "1px solid " + color + "66",
            borderBottom: "1px solid " + color + "66",
            transform: pos.below ? "rotate(-135deg)" : "rotate(45deg)"
          }} />
        </div>,
        document.body
      ) : null}
    </div>
  );
}
